import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { glass } from "./portal";
import { useProject } from "@/lib/portal/useProject";
import { AreaChart, Area, ResponsiveContainer, XAxis, YAxis, Tooltip, PieChart, Pie, Cell, BarChart, Bar } from "recharts";
import { format } from "date-fns";

export const Route = createFileRoute("/_authenticated/portal/analytics")({
  component: AnalyticsPage,
});

const COLORS = ["#e879f9", "#22d3ee", "#a3e635", "#fbbf24"];

function AnalyticsPage() {
  const { data: project } = useProject();
  const { data: snaps } = useQuery({
    queryKey: ["portal", "analytics-full"],
    queryFn: async () => (await supabase.from("analytics_snapshots").select("*").order("snapshot_date")).data ?? [],
    enabled: !!project,
  });
  const { data: report } = useQuery({
    queryKey: ["portal", "report"],
    queryFn: async () => (await supabase.from("monthly_reports").select("*").order("created_at", { ascending: false }).limit(1).maybeSingle()).data,
    enabled: !!project,
  });

  const total = (snaps ?? []).reduce((s, a) => s + a.visitors, 0);
  const views = (snaps ?? []).reduce((s, a) => s + a.page_views, 0);
  const avgSession = Math.round((snaps ?? []).reduce((s, a) => s + a.avg_session_seconds, 0) / Math.max((snaps ?? []).length, 1));
  const trend = (snaps ?? []).map((a) => ({ d: format(new Date(a.snapshot_date), "d/M"), Visitatori: a.visitors, Views: a.page_views }));
  const devicesLast = snaps?.at(-1)?.devices as Record<string, number> | undefined;
  const countriesLast = snaps?.at(-1)?.countries as Record<string, number> | undefined;
  const topPages = (snaps?.at(-1)?.top_pages ?? []) as { path: string; views: number }[];
  const devicesArr = devicesLast ? Object.entries(devicesLast).map(([k, v]) => ({ name: k, value: v })) : [];
  const countriesArr = countriesLast ? Object.entries(countriesLast).map(([k, v]) => ({ name: k, value: v })) : [];

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">Analytics</h1>
        <p className="text-white/60 mt-1">Traffico, dispositivi, paesi, top pages · integrazione GA pronta.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi label="Visitatori 30gg" value={total.toLocaleString("it-CH")} />
        <Kpi label="Page views" value={views.toLocaleString("it-CH")} />
        <Kpi label="Sessione media" value={`${Math.floor(avgSession / 60)}m ${avgSession % 60}s`} />
        <Kpi label="Uptime" value={report?.uptime_percent ? `${report.uptime_percent}%` : "—"} />
      </div>

      <div className={`${glass} p-6`}>
        <div className="text-sm font-semibold mb-4">Traffico ultimi 30 giorni</div>
        <div className="h-64">
          <ResponsiveContainer>
            <AreaChart data={trend}>
              <defs>
                <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#e879f9" stopOpacity={0.6} />
                  <stop offset="100%" stopColor="#e879f9" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="g2" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#22d3ee" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#22d3ee" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="d" stroke="#666" fontSize={11} />
              <YAxis stroke="#666" fontSize={11} />
              <Tooltip contentStyle={{ background: "#0f0f16", border: "1px solid #ffffff20", borderRadius: 12 }} />
              <Area dataKey="Visitatori" stroke="#e879f9" fill="url(#g1)" strokeWidth={2} />
              <Area dataKey="Views" stroke="#22d3ee" fill="url(#g2)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className={`${glass} p-6`}>
          <div className="text-sm font-semibold mb-4">Dispositivi</div>
          <div className="h-56"><ResponsiveContainer>
            <PieChart>
              <Pie data={devicesArr} dataKey="value" nameKey="name" innerRadius={40} outerRadius={70}>
                {devicesArr.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip contentStyle={{ background: "#0f0f16", border: "1px solid #ffffff20", borderRadius: 12 }} />
            </PieChart>
          </ResponsiveContainer></div>
          <div className="flex gap-3 text-xs justify-center mt-2 flex-wrap">
            {devicesArr.map((d, i) => <div key={d.name} className="flex items-center gap-1"><span className="h-2 w-2 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />{d.name} {d.value}%</div>)}
          </div>
        </div>
        <div className={`${glass} p-6`}>
          <div className="text-sm font-semibold mb-4">Paesi</div>
          <div className="h-56"><ResponsiveContainer>
            <BarChart data={countriesArr}>
              <XAxis dataKey="name" stroke="#666" fontSize={11} />
              <YAxis stroke="#666" fontSize={11} />
              <Tooltip contentStyle={{ background: "#0f0f16", border: "1px solid #ffffff20", borderRadius: 12 }} />
              <Bar dataKey="value" fill="#22d3ee" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer></div>
        </div>
        <div className={`${glass} p-6`}>
          <div className="text-sm font-semibold mb-4">Top pages</div>
          <ul className="space-y-2">
            {topPages.map((p) => (
              <li key={p.path} className="flex items-center justify-between text-sm">
                <span className="font-mono text-white/70">{p.path}</span><span className="text-white/50">{p.views}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {report && (
        <div className={`${glass} p-6`}>
          <div className="text-xs uppercase tracking-widest text-fuchsia-300">Report mensile FlashWeb Flex</div>
          <div className="mt-2 grid grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
            <Info label="Backups eseguiti" value={String(report.backups_count ?? 0)} />
            <Info label="Update installati" value={String(report.updates_installed ?? 0)} />
            <Info label="Uptime" value={`${report.uptime_percent ?? 0}%`} />
            <Info label="Report" value={format(new Date(report.report_month), "MMM yyyy")} />
          </div>
          {report.seo_notes && (
            <div className="mt-4 rounded-2xl bg-white/5 border border-white/10 p-4">
              <div className="text-xs uppercase text-white/40 mb-1">Suggerimenti SEO</div>
              <p className="text-sm text-white/80">{report.seo_notes}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Kpi({ label, value }: { label: string; value: string }) {
  return <div className={`${glass} p-4`}><div className="text-xs text-white/50">{label}</div><div className="text-2xl font-semibold mt-1">{value}</div></div>;
}
function Info({ label, value }: { label: string; value: string }) {
  return <div><div className="text-xs text-white/40">{label}</div><div className="font-medium">{value}</div></div>;
}
